import { type ReactNode, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { CircleAlertIcon } from 'lucide-react'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

import { SmartButton } from '@/components/ui-custom/SmartButton'

type DialogType = 'info' | 'confirm'

interface BaseDialogProps {
  trigger: ReactNode
  type?: DialogType
  title?: string
  description?: ReactNode
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void
  onCancel?: () => void
  confirmDisabled?: boolean
  children: ReactNode
  contentClassName?: string
}

export function BaseDialog({
  trigger,
  type = 'info',
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  confirmDisabled,
  contentClassName,
  children,
}: BaseDialogProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)

  const handleConfirm = () => {
    onConfirm?.()
    setOpen(false)
  }

  const handleCancel = () => {
    onCancel?.()
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogOverlay className='bg-gray-300/40' />
      <DialogContent className={contentClassName}>
        <div className='flex flex-col items-center gap-2'>
          {type === 'confirm' && (
            <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border' aria-hidden='true'>
              <CircleAlertIcon className='opacity-80' size={16} />
            </div>
          )}
          <DialogHeader>
            <DialogTitle className='sm:text-center'>{title}</DialogTitle>
            {description && <DialogDescription className='sm:text-center'>{description}</DialogDescription>}
          </DialogHeader>
        </div>
        {children}
        <DialogFooter>
          <DialogClose asChild>
            <SmartButton variant='outline' onClick={handleCancel} className='w-full cursor-pointer'>
              {cancelText ?? t('common:actions.cancel', 'Cancel')}
            </SmartButton>
          </DialogClose>
          {type === 'confirm' && (
            <SmartButton onClick={handleConfirm} disabled={confirmDisabled} className='cursor-pointer'>
              {confirmText ?? t('common:actions.confirm', 'Confirm')}
            </SmartButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
